import { useContext, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  ShareDialogButton,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import type { ShareOption } from "../../../../../components/Buttons/Buttons";
import { LinkIcon, ShareIcon } from "../../../../../components/Icons/Icons";
import type { EntityType } from "../../../../../types/types.MDM";
import { useRoutePath } from "../../../../../util/Routing";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { spacingTokens } from "../../../../../designTokens";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";

const ShareButtonContainer = styled.div`
  svg {
    width: 20px !important;
    height: 20px !important;
  }
`;

const ENTITY_PATH_SEGMENT: Record<EntityType, string> = {
  customer: "customers",
  supplier: "suppliers",
  distributor: "distributors",
};

export const EntityShareButton = ({
  entityId,
  entityType,
  name,
  showLabel,
}: {
  entityId: string;
  entityType: EntityType;
  name: string;
  showLabel?: boolean;
}) => {
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const { getLabel } = useMdmEntityDisplayNames();
  // const { storefront_metadata } = useStoreState();
  const { notifyError, notifySuccess } = useContext(Notifications);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const entityLabel = getLabel(entityType, "singular");
  const internalLink = `${window.location.origin}${adminPath}/mdm/${ENTITY_PATH_SEGMENT[entityType]}/${entityId}`;
  // const shareTitle = name;
  // const shareDescription = `Check out ${name} on ${storefront_metadata.tenant_name}`;

  const copyLink = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notifySuccess(t("Link copied to clipboard"));
    } catch (e) {
      notifyError(t("Could not copy link"));
    }
  };

  const internalItems: ShareOption[] = [
    {
      label: t("Copy link"),
      icon: <LinkIcon fill="#000000" />,
      onClick: () => copyLink(internalLink),
    },
  ];

  // COMMENTED OUT TILL WE ENABLE EXTERNAL SHARING FOR ENTITIES

  // const handleShare = (platform: Platform) => {
  //   let url: string | undefined;
  //   switch (platform) {
  //     case "linkedin":
  //       url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
  //         internalLink
  //       )}&title=${encodeURIComponent(shareTitle)}&summary=${encodeURIComponent(
  //         shareDescription
  //       )}`;
  //       break;
  //     case "whatsapp":
  //       url = `https://api.whatsapp.com/send?text=${encodeURIComponent(
  //         `${shareTitle} — ${internalLink}`
  //       )}`;
  //       break;
  //     case "facebook":
  //       url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
  //         internalLink
  //       )}`;
  //       break;
  //     case "x":
  //       url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
  //         internalLink
  //       )}&text=${encodeURIComponent(shareTitle)}`;
  //       break;
  //   }
  //   if (url) window.open(url, "_blank", "noopener,noreferrer");
  // };

  // const externalItems: ShareOption[] = [
  //   {
  //     label: t("Share on LinkedIn"),
  //     icon: <LinkedinIcon fill="#000000" />,
  //     onClick: () => handleShare("linkedin"),
  //   },
  //   {
  //     label: t("Share on Facebook"),
  //     icon: <FacebookIcon fill="#000000" />,
  //     onClick: () => handleShare("facebook"),
  //   },
  //   {
  //     label: t("Share on WhatsApp"),
  //     icon: <WhatsappIcon fill="#000000" />,
  //     onClick: () => handleShare("whatsapp"),
  //   },
  //   {
  //     label: t("Share on X"),
  //     icon: <TwitterIcon fill="#000000" />,
  //     onClick: () => handleShare("x"),
  //   },
  // ];

  return (
    <ShareButtonContainer>
      <TertiaryButtonLargeV2
        data-alignright
        onClick={() => setDialogOpen(true)}
        style={{ padding: `${spacingTokens.spacing_md} 10px` }}
      >
        <ShareIcon width={20} height={20} />{" "}
        {showLabel && (
          <span style={{ fontWeight: "var(--font_weight_medium)" }}>
            {t("Share")}
          </span>
        )}
      </TertiaryButtonLargeV2>

      <ShareDialogButton
        isOpen={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        heading={t("Share this {{entity}}", { entity: entityLabel })}
        internalItems={internalItems}
        isConnectionOn={false}
        isLinkDisabled={false}
      />
    </ShareButtonContainer>
  );
};
